/**
 * „Wyloguj się" in the `/konto` header, and the confirm it asks first — there is no undo. Shared by
 * the screen and by `/konto/google`, which draws the same header.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { routeForSessionStatus, signOut } from '~/entities/session';
import { ConfirmDialog, type ConfirmPrompt } from '~/shared/ui/ConfirmDialog';
import ghost from '~/shared/ui/ghostButton.module.css';

import { SignOutIcon } from './AccountIcons';
import styles from './AccountScreen.module.css';

export function SignOutButton() {
  const { t } = useTranslation();
  const [signOutPrompt, setSignOutPrompt] = useState<ConfirmPrompt | null>(null);
  const [signingOut, setSigningOut] = useState(false);

  return (
    <>
      <button
        type="button"
        className={`${String(ghost.ghost)} ${String(styles.accountButton)} ${String(styles.signOut)}`}
        onClick={() => {
          setSignOutPrompt({
            title: t('web:account.signOut.confirmTitle'),
            description: t('web:account.signOut.confirmBody'),
            confirmLabel: t('web:account.signOut.action'),
            cancelLabel: t('common.cancel'),
          });
        }}
      >
        <SignOutIcon className={styles.signOutIcon} />
        {t('web:account.signOut.action')}
      </button>

      <ConfirmDialog
        prompt={signOutPrompt === null ? null : { ...signOutPrompt, busy: signingOut }}
        onCancel={() => {
          setSignOutPrompt(null);
        }}
        onConfirm={() => {
          setSigningOut(true);
          // The session's own routing table decides where a signed-out trainer belongs, so the
          // destination is not restated. `signOut` cancels and clears the query cache first.
          //
          // A local sign-out that fails still has to land on the sign-in route: a busy button that
          // never resolves is worse than a session the route's own gate will re-check on arrival.
          void signOut()
            .catch(() => undefined)
            .finally(() => {
              window.location.assign(routeForSessionStatus('signedOut') ?? '/sign-in');
            });
        }}
      />
    </>
  );
}
